import { useState } from 'react';
import BottomSheet from './ui/BottomSheet';

interface Member {
  id: string;
  display_name: string;
  avatar_emoji: string;
  left_at?: string | null;
}

interface MemberChecklistSheetProps {
  isOpen: boolean;
  onClose: () => void;
  members: Member[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  amountCents: number;
  isPro: boolean;
  onUpgradeAttempted: () => void;
}

export default function MemberChecklistSheet({
  isOpen,
  onClose,
  members,
  selectedIds,
  onSelectionChange,
  amountCents,
  isPro,
  onUpgradeAttempted,
}: MemberChecklistSheetProps) {
  const [customAmounts, setCustomAmounts] = useState<Record<string, number>>({});

  const toggleMember = (memberId: string) => {
    if (selectedIds.includes(memberId)) {
      if (selectedIds.length <= 1) return;
      onSelectionChange(selectedIds.filter(id => id !== memberId));
    } else {
      onSelectionChange([...selectedIds, memberId]);
    }
  };

  const includedCount = selectedIds.length;
  const perPersonCents = includedCount > 0 ? Math.round(amountCents / includedCount) : 0;
  const currencySymbol = '$';

  const formatCents = (cents: number) => `${currencySymbol}${(cents / 100).toFixed(2)}`;

  const handleCustomAmountChange = (memberId: string, value: string) => {
    if (!isPro) {
      onUpgradeAttempted();
      return;
    }
    const num = parseFloat(value) || 0;
    setCustomAmounts(prev => ({ ...prev, [memberId]: Math.round(num * 100) }));
  };

  const totalCustomCents = Object.values(customAmounts).reduce((a, b) => a + b, 0);
  const overBudget = totalCustomCents > amountCents;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Split Between">
      <div className="space-y-3">
        <p className="text-sm text-muted">
          {includedCount} people included
        </p>
        {members.filter(m => !m.left_at).map(member => {
          const isSelected = selectedIds.includes(member.id);
          return (
            <div key={member.id} className="flex items-center gap-3">
              <button
                onClick={() => toggleMember(member.id)}
                className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 touch-target ${
                  isSelected
                    ? 'bg-primary border-primary'
                    : 'border-muted/40'
                }`}
              >
                {isSelected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
              <span className="text-lg">{member.avatar_emoji || '👤'}</span>
              <span className="font-medium text-sm flex-1">{member.display_name}</span>
              {isSelected && isPro && (
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={formatCents(perPersonCents)}
                  value={customAmounts[member.id] ? (customAmounts[member.id] / 100).toString() : ''}
                  onChange={e => handleCustomAmountChange(member.id, e.target.value)}
                  className="w-20 bg-card border border-border rounded-lg px-3 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              )}
              {isSelected && !isPro && (
                <span className="text-sm tabular-nums text-muted">{formatCents(perPersonCents)}</span>
              )}
            </div>
          );
        })}
        {customAmounts && isPro && Object.keys(customAmounts).length > 0 && (
          <div className={`text-sm mt-2 p-2 rounded-lg ${overBudget ? 'bg-danger/10 text-danger' : 'bg-card'}`}>
            {overBudget
              ? `Total (${formatCents(totalCustomCents)}) exceeds ${formatCents(amountCents)}`
              : `${formatCents(amountCents - totalCustomCents)} remaining`}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
